export default function(ngModule) {
    /**
     * @ngdoc service
     * @name eventix.common.models.ProductGroup
     * @kind class
     * @description
     * A model that allows one to place restrictions on products attached
     * to a ticket. The group defines a minimum, maximum or exact amount
     * of products required for purchasing a ticket. Group contains a
     * subset of products in the ticket.
     */
    ngModule.factory('ProductGroup', function(BaseClass,$q, $http, Product, Ticket, Rollbar) {

        /**
         * Constructor function
         *
         * @param {Object} attributes Initial attributes for the model
         */
        function ProductGroup(attributes) {
            _.assign(this,attributes);
            if(this.members)
                this.products = this.members;
            // Due to a restriction in Eloquent ORM, if we GET a ProductGroup, its
            // `products` are under the property `members`
            Object.defineProperty(this, 'required', {
                configurable: true,
                get: function() {
                    return this.isRequiredGroup();
                },
                set: function(v) {
                    if(v) {
                        this.uniqueness = 1;
                        this.min_bound = this.products.length;
                        this.max_bound = this.products.length;
                    } else {
                        this.min_bound = 0;
                        this.max_bound = 0;
                        this.uniqueness = 0;
                    }
                }
            });
        }
        ProductGroup.modelName = 'ProductGroup';

        BaseClass.apply(ProductGroup);

        ProductGroup.validates({
            max_bound: {
                required: true,
                minimumNumberMaxBound: {
                    bound: 0,
                    message: 'Amount should be at least 0 product',
                    validator: function(value, instance, field) {
                        if(_.isUndefined(value))
                            return true;
                        return value >= this.bound;
                    }
                },
                shouldEqualOrExceedMinBound: {
                    message: 'Maximum bound should equal or exceed minimum bound',
                    validator: function(value, instance, field) {
                        if(_.isUndefined(value) || value === 0)
                            return true;
                        return value >= (instance.min_bound || 0);
                    }
                },
                integer: true
            },
            min_bound: {
                required: true,
                shouldEqualOrBeLessThanMaxBound: {
                    message: 'Minimum bound should equal or be less than maximum bound',
                    validator: function(value, instance, field) {
                        if(_.isUndefined(value))
                            return true;
                        return value <= (instance.max_bound || 0);
                    }
                },
                minimumNumberMinBound: {
                    bound: 0,
                    message: 'Amount should be at least 1 product',
                    validator: function(value, instance, field) {
                        if(_.isUndefined(value))
                            return true;
                        return value >= this.bound;
                    }
                },
                integer: true
            },
            products: {
                minimumElements: {
                    bound: 1,
                    message: 'Select at least 1 product',
                    validator: function(value, instance, field) {
                        if(!_.isArray(value))
                            return false;
                        return value.length >= this.bound;
                    }
                }
            }
        });

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductGroup#getMinimumProductSet
         * @methodOf eventix.common.models.ProductGroup
         * @inner
         * @description
         * Get the product groups smallest, cheapest set of products that satisfy
         * the group bounds
         * @param {Boolean} [cacheOnly=true] Whether to allow function to hit server or rely on cache
         * @returns {Promise} Resolves with an array of Products
         */
        ProductGroup.prototype.getMinimumProductSet = function(cacheOnly = true) {
            if(this.min_bound === 0)
                return $q.resolve([]);
            const group = this;
            let ticket;
            let products;
            return Ticket.get({ guid: this.ticket_id }).then(_ticket => {
                ticket = _ticket;
                if(!ticket) {
                    var error = new Error('Could not find ticket for product group: ' + JSON.stringify(this));
                    error.message = 'Could not find ticket for product group: ' + JSON.stringify(this);
                    Rollbar.error('Could not find ticket for product group: ' + JSON.stringify(this), error);
                    return $q.reject(error);
                }
                return ticket.$queryProduct(cacheOnly);
            }).then(_products => {
                products = _.filter(_products, p => _.includes(group.products, p.guid));
                let sortedProductSet = _.orderBy(products, ['ticketPrice'], ['asc']);
                let taken = {};
                let requiredSet = _.times(group.min_bound, n => {
                    let product = _.find(sortedProductSet, p => !group.uniqueness || (taken[p.guid] || 0) < group.uniqueness);
                    if(!product) {
                        let error = new Error(`Could not construct a valid group for: ${JSON.stringify(this)}`);
                        Rollbar.error('Could not construct valid group', error);
                        throw error;
                    }
                    if(!taken[product.guid])
                        taken[product.guid] = 0;
                    taken[product.guid] += 1;
                    return Product.new(product);
                });
                return requiredSet;
            }).catch(() => []);
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductGroup#getRequiredProductSet
         * @methodOf eventix.common.models.ProductGroup
         * @inner
         * @description
         * Get the products this group can never be valid without
         * @param {Boolean} [cacheOnly=true] Whether to allow function to hit server or rely on cache
         * @returns {Promise} Resolves with an array of Products or rejects when this group is not a unique requirement of all members
         */
        ProductGroup.prototype.getRequiredProductSet = function(cacheOnly = true) {
            if(!this.isRequiredGroup())
                return $q.reject(new Error('NonRequiredGroup'));
            const group = this;
            let ticket;
            let products;
            return Ticket.get({ guid: this.ticket_id }).then(_ticket => {
                ticket = _ticket;
                if(!ticket) {
                    var error = new Error('Could not find ticket for product group: ' + JSON.stringify(this));
                    error.message = 'Could not find ticket for product group: ' + JSON.stringify(this);
                    Rollbar.error('Could not find ticket for product group: ' + JSON.stringify(this), error);
                    return $q.reject(error);
                }
                return ticket.$queryProduct(cacheOnly);
            }).then(_products => {
                return _.filter(_products, p => _.includes(group.products, p.guid));
            });
        };

        ProductGroup.prototype.isRequiredGroup = function() {
            return this.min_bound === this.max_bound && this.min_bound === this.products.length && this.uniqueness === 1;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductGroup#calculateMinimumPrice
         * @methodOf eventix.common.models.ProductGroup
         * @inner
         * @description
         * Calculate group minimum price. It's not reliable by its very nature
         * because you don't know which products are actually still available, so
         * it finds the cheapest set of products that satisfy the group bounds.
         * @param {Boolean} [cacheOnly=true] Whether to allow function to hit server or rely on cache
         * @returns {Promise} Resolves with a minumum price
         */
        ProductGroup.prototype.calculateMinimumPrice = function(cacheOnly = true) {
            return this.getMinimumProductSet().then(products => {
                return _.reduce(products, (t,p) => t + _.toInteger(p.ticketPrice), 0);
            });
        };

        ProductGroup.configureApi('/ticket/:ticket_id/groups/:guid');
        ProductGroup.prototype.toString = function() { return this.name; };

        // We're keeping an array of ProductGuids under this.products, the following belongsToMany relation is to
        // create a consistent interface. It can readily be used in CrudCtrl service, or the childSelector component
        ProductGroup.belongsToMany('Product',
            'children',
            '/ticket/:ticket_id/groups/:guid/products/^guid',
            [{
                type: 'query',
                fn: function(modelName, url, group) {
                    let promises = _.map(group.products, productGuid => Product.get({ guid: productGuid }));
                    return $q.all(promises);
                }
            }, {
                type: 'attach',
                fn: function(modelName, url, group, product) {
                    if(!angular.isArray(group.products))
                        group.products = [];
                    group.products.push(product.guid);
                    return $q.resolve();
                }
            }, {
                type: 'detach',
                fn: function(modelName, url, group, product) {
                    group.products = _.pull(group.products, product.guid);
                    return $q.resolve();
                }
            }]
        );
        ProductGroup.belongsToMany('MetaData',
            'metadata',
            '/ticket/:ticket_id/groups/:guid/metadata/^guid',
            [
                { type: 'query' },
                { type: 'attach' },
                { type: 'detach' }
            ]);

        return ProductGroup;
    });
}
